import { useTranslation } from 'react-i18next'
import { LandingIcon, type LandingIconName } from './LandingIcons.js'

type DishArtVariant = 'pasta' | 'bowl' | 'pastry'

const HERO_CATEGORIES = [
  'landing.hero.preview.categoryAll',
  'landing.hero.preview.categoryBreakfast',
  'landing.hero.preview.categoryHot',
  'landing.hero.preview.categoryDessert',
] as const

const HERO_DISHES: {
  art: DishArtVariant
  nameKey: string
  metaKey: string
  priceKey: string
}[] = [
  {
    art: 'pasta',
    nameKey: 'landing.hero.preview.dish1Name',
    metaKey: 'landing.hero.preview.dish1Meta',
    priceKey: 'landing.hero.preview.dish1Price',
  },
  {
    art: 'bowl',
    nameKey: 'landing.hero.preview.dish2Name',
    metaKey: 'landing.hero.preview.dish2Meta',
    priceKey: 'landing.hero.preview.dish2Price',
  },
  {
    art: 'pastry',
    nameKey: 'landing.hero.preview.dish3Name',
    metaKey: 'landing.hero.preview.dish3Meta',
    priceKey: 'landing.hero.preview.dish3Price',
  },
]

const DASHBOARD_FILTERS = [
  'landing.how.preview.filterAll',
  'landing.how.preview.filterNew',
  'landing.how.preview.filterPreparing',
  'landing.how.preview.filterDone',
] as const

const DASHBOARD_ROWS: {
  tone: 'new' | 'preparing' | 'ready'
  icon: LandingIconName
  typeKey: string
  metaKey: string
  timeKey: string
  statusKey: string
}[] = [
  {
    tone: 'new',
    icon: 'bag',
    typeKey: 'landing.how.preview.row1Type',
    metaKey: 'landing.how.preview.row1Meta',
    timeKey: 'landing.how.preview.row1Time',
    statusKey: 'landing.how.preview.row1Status',
  },
  {
    tone: 'preparing',
    icon: 'delivery',
    typeKey: 'landing.how.preview.row2Type',
    metaKey: 'landing.how.preview.row2Meta',
    timeKey: 'landing.how.preview.row2Time',
    statusKey: 'landing.how.preview.row2Status',
  },
  {
    tone: 'ready',
    icon: 'bag',
    typeKey: 'landing.how.preview.row3Type',
    metaKey: 'landing.how.preview.row3Meta',
    timeKey: 'landing.how.preview.row3Time',
    statusKey: 'landing.how.preview.row3Status',
  },
]

export function DishArt({ variant }: { variant: DishArtVariant }) {
  if (variant === 'pasta') {
    return (
      <svg viewBox="0 0 160 120" fill="none" aria-hidden="true" focusable="false">
        <ellipse cx="80" cy="64" rx="55" ry="40" fill="#FFFDF8" />
        <ellipse cx="80" cy="64" rx="45" ry="32" fill="#F2C86B" />
        <path
          d="M50 66c10-11 22-7 30 1s18 10 29 2M49 57c12-8 22-2 30 4s20 8 30 0M59 77c8-6 16-2 22 4s16 6 24 0"
          stroke="#D79A31"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <circle cx="68" cy="55" r="7" fill="#D9532D" />
        <circle cx="93" cy="73" r="6" fill="#C74926" />
        <path d="M87 47c6 2 10 6 12 12" stroke="#527E40" strokeWidth="4" strokeLinecap="round" />
        <ellipse cx="80" cy="96" rx="40" ry="5" fill="#E4D2B6" opacity=".55" />
      </svg>
    )
  }

  if (variant === 'bowl') {
    return (
      <svg viewBox="0 0 160 120" fill="none" aria-hidden="true" focusable="false">
        <ellipse cx="80" cy="58" rx="48" ry="15" fill="#F1E4D0" />
        <path d="M33 58h94c-2 27-21 43-47 43S35 85 33 58Z" fill="#FFFDF8" />
        <path d="M41 61c6 23 20 33 39 33s33-10 39-33" stroke="#EADCC6" strokeWidth="2" />
        <circle cx="67" cy="52" r="11" fill="#7FA34A" />
        <circle cx="91" cy="48" r="9" fill="#E2A63C" />
        <circle cx="82" cy="59" r="8" fill="#D96A3B" />
        <circle cx="101" cy="60" r="6" fill="#A9C46B" />
        <path d="M55 43c5-9 14-11 21-6" stroke="#5C7F3A" strokeWidth="4" strokeLinecap="round" />
        <ellipse cx="80" cy="96" rx="41" ry="5" fill="#E4D2B6" opacity=".55" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 160 120" fill="none" aria-hidden="true" focusable="false">
      <ellipse cx="80" cy="66" rx="47" ry="34" fill="#FFFDF8" />
      <path
        d="M45 69c0-17 15-29 35-29s35 12 35 29c-9 7-20 10-35 10s-26-3-35-10Z"
        fill="#E9AA4C"
      />
      <path
        d="M59 49c6 8 10 16 10 27M80 43c0 11 0 21 0 33M101 49c-6 8-10 16-10 27"
        stroke="#C8872C"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path d="M45 69c9-4 18-6 35-6s26 2 35 6" stroke="#B97624" strokeWidth="3" strokeLinecap="round" />
      <ellipse cx="80" cy="96" rx="38" ry="5" fill="#E4D2B6" opacity=".55" />
    </svg>
  )
}

export function HeroStorefront() {
  const { t } = useTranslation()

  return (
    <div className="mf-hero-visual">
      <div className="mf-window" aria-hidden="true">
        <div className="mf-window-bar">
          <span className="mf-window-dots">
            <i />
            <i />
            <i />
          </span>
          <span className="mf-window-url">
            <LandingIcon name="globe" />
            magic-food.online/your-restaurant
          </span>
          <span className="mf-window-label">{t('landing.hero.previewLabel')}</span>
        </div>

        <div className="mf-window-body">
          <div className="mf-store-head">
            <span className="mf-store-avatar">M</span>
            <span className="mf-store-title">
              <span className="mf-store-name">{t('landing.hero.preview.brand')}</span>
              <span className="mf-store-state">
                <i className="mf-status-dot" />
                {t('landing.hero.preview.status')}
              </span>
            </span>
            <span className="mf-store-cart">{t('landing.hero.preview.cart')}</span>
          </div>

          <div className="mf-store-chips">
            {HERO_CATEGORIES.map((key, index) => (
              <span
                key={key}
                className={index === 0 ? 'mf-store-chip mf-store-chip--active' : 'mf-store-chip'}
              >
                {t(key)}
              </span>
            ))}
          </div>

          <div className="mf-store-grid">
            {HERO_DISHES.map(dish => (
              <div className="mf-store-card" key={dish.nameKey}>
                <div className={`mf-store-art mf-store-art--${dish.art}`}>
                  <DishArt variant={dish.art} />
                </div>
                <span className="mf-store-dish">{t(dish.nameKey)}</span>
                <span className="mf-store-meta">{t(dish.metaKey)}</span>
                <span className="mf-store-price-row">
                  <strong>{t(dish.priceKey)}</strong>
                  <span className="mf-store-plus">+</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mf-float mf-float--order" aria-hidden="true">
        <span className="mf-float-icon mf-float-icon--green">
          <LandingIcon name="check" />
        </span>
        <span className="mf-float-copy">
          <span className="mf-float-title">{t('landing.hero.preview.orderTitle')}</span>
          <span className="mf-float-meta">{t('landing.hero.preview.orderMeta')}</span>
        </span>
        <span className="mf-pill mf-pill--green">{t('landing.hero.preview.orderStatus')}</span>
      </div>

      <div className="mf-float mf-float--delivery" aria-hidden="true">
        <span className="mf-float-icon mf-float-icon--gold">
          <LandingIcon name="delivery" />
        </span>
        <span className="mf-float-copy">
          <span className="mf-float-title">{t('landing.hero.preview.deliveryTitle')}</span>
          <span className="mf-float-meta">{t('landing.hero.preview.deliveryMeta')}</span>
        </span>
      </div>
    </div>
  )
}

export function DashboardMockup() {
  const { t } = useTranslation()

  return (
    <div className="mf-dash" aria-hidden="true">
      <div className="mf-dash-head">
        <span className="mf-dash-heading">
          <span className="mf-dash-kicker">{t('landing.how.preview.label')}</span>
          <span className="mf-dash-title">{t('landing.how.preview.title')}</span>
        </span>
        <span className="mf-dash-live">
          <i />
          {t('landing.how.preview.live')}
        </span>
      </div>

      <div className="mf-dash-filters">
        {DASHBOARD_FILTERS.map((key, index) => (
          <span
            key={key}
            className={index === 0 ? 'mf-dash-filter mf-dash-filter--active' : 'mf-dash-filter'}
          >
            {t(key)}
          </span>
        ))}
      </div>

      <div className="mf-dash-list">
        {DASHBOARD_ROWS.map(row => (
          <div className="mf-dash-row" key={row.typeKey}>
            <span className={`mf-dash-icon mf-dash-icon--${row.tone}`}>
              <LandingIcon name={row.icon} />
            </span>
            <span className="mf-dash-main">
              <strong>{t(row.typeKey)}</strong>
              <small>
                {t(row.metaKey)} · {t(row.timeKey)}
              </small>
            </span>
            <span className={`mf-pill mf-pill--${row.tone}`}>{t(row.statusKey)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function CheckoutMockup() {
  const { t } = useTranslation()

  return (
    <div className="mf-guest-visual">
      <div className="mf-checkout" aria-hidden="true">
        <div className="mf-checkout-head">
          <span className="mf-checkout-title">{t('landing.guests.preview.title')}</span>
          <span className="mf-checkout-lock">
            <LandingIcon name="lock" />
          </span>
        </div>

        <div className="mf-switch">
          <span className="mf-switch-item mf-switch-item--active">
            {t('landing.guests.preview.pickup')}
          </span>
          <span className="mf-switch-item">{t('landing.guests.preview.delivery')}</span>
        </div>

        <div className="mf-field-row">
          <span className="mf-field">
            <small>{t('landing.guests.preview.nameLabel')}</small>
            <b>{t('landing.guests.preview.nameValue')}</b>
          </span>
          <span className="mf-field">
            <small>{t('landing.guests.preview.phoneLabel')}</small>
            <b>{t('landing.guests.preview.phoneValue')}</b>
          </span>
        </div>

        <div className="mf-summary">
          <span className="mf-summary-title">{t('landing.guests.preview.summary')}</span>
          <span className="mf-summary-row">
            <span>{t('landing.guests.preview.item1')}</span>
            <span>{t('landing.guests.preview.item1Meta')}</span>
          </span>
          <span className="mf-summary-row">
            <span>{t('landing.guests.preview.item2')}</span>
            <span>{t('landing.guests.preview.item2Meta')}</span>
          </span>
          <span className="mf-summary-total">
            <span>{t('landing.guests.preview.total')}</span>
            <strong>{t('landing.guests.preview.totalValue')}</strong>
          </span>
        </div>

        <span className="mf-checkout-submit">{t('landing.guests.preview.submit')}</span>
      </div>

      <div className="mf-track" aria-hidden="true">
        <div className="mf-track-head">
          <span className="mf-track-check">
            <LandingIcon name="check" />
          </span>
          <span className="mf-track-title">{t('landing.guests.preview.trackTitle')}</span>
        </div>
        <div className="mf-track-progress">
          <span className="mf-track-dot mf-track-dot--done" />
          <span className="mf-track-line mf-track-line--done" />
          <span className="mf-track-dot mf-track-dot--active" />
          <span className="mf-track-line" />
          <span className="mf-track-dot" />
        </div>
        <div className="mf-track-labels">
          <span>{t('landing.guests.preview.trackStep1')}</span>
          <span>{t('landing.guests.preview.trackStep2')}</span>
          <span>{t('landing.guests.preview.trackStep3')}</span>
        </div>
        <p className="mf-track-hint">{t('landing.guests.preview.trackHint')}</p>
      </div>
    </div>
  )
}
